import ResponseMessage from "@/components/Error/ResponseMessage";
import Input from "@/components/Input/Input";
import { HStack, VStack } from "@/components/Layout";
import { Label } from "@/components/ui/label";
import { LANGUAGE_CONFIG, defaultNS, useTranslation } from "@/lib/i18n";
import type { FormContextErrors } from "./-xstate/add-person-to-send-invitation-dialog.machine";

const NameField = ({
  label,
  value,
  onChange,
  errorMessage,
  isOptional,
  optionalLabel,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  errorMessage?: string;
  isOptional?: boolean;
  optionalLabel?: string;
  className?: string;
}) => (
  <VStack className={`gap-1.5 ${className || ""}`}>
    <HStack className="gap-2 items-center">
      <Label className="text-xs font-semibold text-neutral-muted-on-fill">
        {label}
      </Label>
      {isOptional && optionalLabel && (
        <Label className="text-xs text-neutral-muted-on-fill">
          {optionalLabel}
        </Label>
      )}
    </HStack>

    <Input value={value} onChange={(e) => onChange(e.target.value)} />

    {!isOptional && errorMessage && (
      <ResponseMessage>{errorMessage}</ResponseMessage>
    )}
  </VStack>
);

const AddPersonToSendInvitationDialogNameFormItem = ({
  givenName,
  setGivenName,
  familyName,
  setFamilyName,
  middleName,
  setMiddleName,
  errors,
}: {
  givenName: string;
  setGivenName: (value: string) => void;
  familyName: string;
  setFamilyName: (value: string) => void;
  middleName: string;
  setMiddleName: (value: string) => void;
  errors: FormContextErrors;
}) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix:
      "pages.settings.persons.account.tabFilterBar.addPersonToSendInvitationDialog",
  });

  const { i18n } = useTranslation();
  const isJapaneseLocale = i18n.language === LANGUAGE_CONFIG["ja-JP"].code;

  /**
   * NOTE: 姓は locale が ja-JP 以外のみ optional
   * NOTE: 姓は locale が ja-JP のみ error 表示
   */
  const mainNameFields = isJapaneseLocale
    ? [
        {
          label: t("lastNameOrFamilyName"),
          isOptional: !isJapaneseLocale,
          optionalLabel: t("optionalLabel"),
          value: familyName,
          onChange: setFamilyName,
          error: errors.familyName,
        },
        {
          label: t("firstOrGivenName"),
          value: givenName,
          onChange: setGivenName,
          error: errors.givenName,
        },
      ]
    : [
        {
          label: t("firstOrGivenName"),
          value: givenName,
          onChange: setGivenName,
          error: errors.givenName,
        },
        {
          label: t("lastNameOrFamilyName"),
          isOptional: isJapaneseLocale,
          optionalLabel: t("optionalLabel"),
          value: familyName,
          onChange: setFamilyName,
          error: errors.familyName,
        },
      ];

  return (
    <>
      <HStack className="justify-between">
        {mainNameFields.map((field, index) => (
          <NameField
            key={index}
            {...field}
            errorMessage={field.error && t(`validationErrors.${field.error}`)}
          />
        ))}
      </HStack>
      <HStack className="justify-between">
        <NameField
          label={t("middleName")}
          value={middleName}
          onChange={setMiddleName}
          isOptional={true}
          optionalLabel={t("optionalLabel")}
        />
      </HStack>
    </>
  );
};

export default AddPersonToSendInvitationDialogNameFormItem;
